'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Cookies from 'js-cookie';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Loader from './Loader';
import { useCityStore } from '@/store/useCityStore';

// Constants
// const cities = [
//     { id: 1, name: 'Chennai', slug: 'chennai' },
//     { id: 2, name: 'Bangalore', slug: 'bangalore' },
//     { id: 3, name: 'Hyderabad', slug: 'hyderabad' },
//     { id: 4, name: 'Mumbai', slug: 'mumbai' },
//     { id: 5, name: 'Delhi', slug: 'delhi' },
//     { id: 6, name: 'Pune', slug: 'pune' },
// ];

const buyMenuData = {
    left: {
        title: 'Popular Choices',
        links: [
            { label: 'Ready to Move', href: '/buy?status=ready' },
            { label: 'Owner Properties', href: '/buy?postedBy=owner' },
            { label: 'Budget Homes', href: '/buy?budgetMax=50' },
            { label: 'Premium/Luxury', href: '/buy?budgetMin=150' },
            { label: 'New Projects', href: '/projects?status=new' },
        ]
    },
    right: {
        title: 'Property Type',
        links: [
            { label: 'Flats / Apartments', href: '/buy?type=apartment' },
            { label: 'Independent House', href: '/buy?type=house' },
            { label: 'Villas', href: '/buy?type=villa' },
            // { label: 'Plots / Land', href: '/buy?type=plot' },
            { label: 'Commercial', href: '/buy?type=commercial' },
        ]
    }
};

const rentMenuData = {
    left: {
        title: 'Popular Choices',
        links: [
            { label: 'Owner Properties', href: '/rent?postedBy=owner' },
            { label: 'Verified Listings', href: '/rent?verified=true' },
            { label: 'Furnished Homes', href: '/rent?furnishing=furnished' },
            { label: 'Bachelor Friendly', href: '/rent?tenant=bachelor' },
            { label: 'Immediately Available', href: '/rent?availability=immediate' },
        ]
    },
    right: {
        title: 'Budget & Type',
        links: [
            { label: 'Under ₹15,000', href: '/rent?budgetMax=15000' },
            { label: '₹15,000 - ₹30,000', href: '/rent?budgetMin=15000&budgetMax=30000' },
            { label: 'Apartments', href: '/rent?type=apartment' },
            { label: 'Independent House', href: '/rent?type=house' },
            { label: 'Shared / PG', href: '/rent?type=pg' },
        ]
    }
};

const userMenuGroups = [
    {
        title: 'My Account',
        path: 'my/activity',
        links: [
            { label: 'My Activity', href: '/my/activity', icon: '📊' },
            { label: 'My Profile', href: '/my/profile', icon: '👤' },
            { label: 'My Alerts', href: '/my/alerts', icon: '🔔' },
        ]
    },
    {
        title: 'Seller Account',
        path: 'seller/dashboard',
        links: [
            { label: 'Dashboard', href: '/seller/dashboard', icon: '📈' },
            { label: 'Projects', href: '/seller/projects', icon: '👥' },
            // { label: 'My Plans', href: '/seller/plans', icon: '💎' },
            { label: 'My Leads', href: '/seller/leads', icon: '👥' },
            { label: 'Analytics', href: '/seller/analytics', icon: '📊' },
            // { label: 'Billing', href: '/seller/billing', icon: '🧾' },
            { label: 'Profile', href: '/seller/profile', icon: '🆔' },
        ]
    }
];

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null); // 'buy', 'rent', 'user' or null
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState("");
    const [isScrolled, setIsScrolled] = useState(false);
    const pathname = usePathname();
    const router = useRouter();
    const hasMounted = useRef(false);
    const searchParams = useSearchParams();
    const cityId = searchParams.get("cityId");

    /* ---------------- CITY STORE ---------------- */
    const {
        cities,
        localities,
        selectedCity,
        selectedLocalities,
        citySearch,
        localitySearch,
        isLoading,
        setCitySearch,
        setLocalitySearch,
        setSelectedCity,
        setSelectedLocalities,
        addLocality,
        removeLocality,
        removeLastLocality,
        fetchCities,
        fetchLocalities,
    } = useCityStore();

    // console.log(user?.name, "user");

    /* ---------------- SEARCH STATE ---------------- */
    const cityRef = useRef(null);
    const localityRef = useRef(null);

    const [showCityList, setShowCityList] = useState(false);
    const [cityIndex, setCityIndex] = useState(-1);

    const [showLocalityList, setShowLocalityList] = useState(false);
    const [localityIndex, setLocalityIndex] = useState(-1);

    const isShowingSelectedCity = citySearch && cities.some(c => c.id === selectedCity?.id && c.name.toLowerCase() === citySearch.toLowerCase());
    const filteredCities = isShowingSelectedCity
        ? cities
        : cities.filter(c => c.name.toLowerCase().includes((citySearch || '').toLowerCase()));

    const localitySuggestions = localities.filter(l => {
        const matchesSearch = (l?.name || '').toLowerCase().includes((localitySearch || '').toLowerCase());
        const notSelected = !selectedLocalities.some(sl => sl.id === l.id);
        return matchesSearch && notSelected;
    });

    console.log('city is :' + selectedCity?.name + 'city id is :' + cityId);
    console.log(user);

    // Check login state on mount
    useEffect(() => {
        const role = Cookies.get('role');
        const token = Cookies.get('token');
        const name = Cookies.get('name');
        const mobile = Cookies.get('mobile');
        const id = Cookies.get('id');
        if (hasMounted.current) return;
        hasMounted.current = true;
        fetchCities(cityId, searchParams.get("localities"));
        if (token) {
            setUser({ role, name, mobile, id });
            setIsLoggedIn(true);
            setUserRole(role.replace(/^"|"$/g, ''));
            console.log("user Role: " + role.replace(/^"|"$/g, ''));
        }
    }, []);


    /* ---------------- EFFECTS AND HANDLERS ---------------- */
    useEffect(() => {
        const close = (e) => {
            if (cityRef.current && !cityRef.current.contains(e.target)) {
                setShowCityList(false);
                setCityIndex(-1);
            }
            if (localityRef.current && !localityRef.current.contains(e.target)) {
                setShowLocalityList(false);
                setLocalityIndex(-1);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    useEffect(() => {
        if (cityIndex >= 0) {
            const el = document.getElementById(`nav-city-item-${cityIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [cityIndex]);

    useEffect(() => {
        if (localityIndex >= 0) {
            const el = document.getElementById(`nav-locality-item-${localityIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [localityIndex]);

    const handleCityKeyDown = async (e) => {
        if (!showCityList) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') setShowCityList(true);
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCityIndex((prev) => (prev < filteredCities.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCityIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (cityIndex >= 0 && cityIndex < filteredCities.length) {
                const selected = filteredCities[cityIndex];
                setCitySearch(selected.name);
                setSelectedCity(selected);
                setShowCityList(false);
                setCityIndex(-1);
                fetchLocalities(selected.id);
                setSelectedLocalities([]);
                setLocalitySearch('');
            }
        } else if (e.key === 'Escape') {
            setShowCityList(false);
            setCityIndex(-1);
        }
    };

    const handleLocalityKeyDown = (e) => {
        if (e.key === 'Backspace' && !localitySearch && selectedLocalities.length > 0) {
            removeLastLocality();
            return;
        }
        if (!showLocalityList) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') setShowLocalityList(true);
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setLocalityIndex((prev) => (prev < localitySuggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setLocalityIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (localityIndex >= 0 && localityIndex < localitySuggestions.length) {
                const selected = localitySuggestions[localityIndex];
                addLocality(selected);
                setLocalitySearch('');
                setLocalityIndex(-1);
            }
        } else if (e.key === 'Escape') {
            setShowLocalityList(false);
            setLocalityIndex(-1);
        }
    };

    const handleLogout = () => {
        //localStorage.removeItem('user');
        Cookies.remove('role');
        Cookies.remove('token');
        Cookies.remove('name');
        Cookies.remove('mobile');
        Cookies.remove('id');
        setIsLoggedIn(false);
        setUser(null);
        setActiveDropdown(null);
        router.push('/');
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const [selectedCategory, setSelectedCategory] = useState({ label: 'Buy', value: 'buy' });
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);

    const handleNavbarSearch = (e) => {
        if (e) e.preventDefault();
        const city = selectedCity?.slug || selectedCity?.name?.toLowerCase() || 'all';
        const basePath = `/${selectedCategory.value}`;
        const params = new URLSearchParams();

        if (selectedLocalities.length > 0) {
            const localitiesSlugs = selectedLocalities.map(l => l.slug || l.name).join(',');
            params.set('q', localitiesSlugs);
        } else if (localitySearch) {
            params.set('q', localitySearch);
        }

        if (selectedCity?.id) params.set('cityId', selectedCity.id);
        if (selectedLocalities.length > 0) params.set('localities', selectedLocalities.map(l => l.id).join('-'));
        router.push(`${basePath}/${city}?${params.toString()}`);
    };

    // Initial check for category based on URL
    useEffect(() => {
        if (pathname.includes('/rent')) setSelectedCategory({ label: 'Rent', value: 'rent' });
        else if (pathname.includes('/projects')) setSelectedCategory({ label: 'Projects', value: 'projects' });
        else if (pathname.includes('/plots')) setSelectedCategory({ label: 'Plots', value: 'plots' });
        else if (pathname.includes('/civil-services')) setSelectedCategory({ label: 'Civil Services', value: 'civil' });
        else setSelectedCategory({ label: 'Buy', value: 'buy' });
    }, [pathname]);

    const handleMouseEnter = (menu) => setActiveDropdown(menu);
    const handleMouseLeave = () => setActiveDropdown(null);

    // Dynamic Navbar Classes
    const isHome = pathname === '/';
    const isSellerPage = pathname.startsWith('/seller');
    const isProtectedPage = pathname.startsWith('/my') || isSellerPage;
    const isTransparent = isHome && !isScrolled;

    const navbarClasses = `navbar transition-all duration-300 z-50 w-full ${isTransparent
        ? 'absolute top-0 left-0 bg-gradient-to-b from-black/80 to-transparent border-b-0 shadow-none'
        : 'sticky top-0 bg-primary shadow-md border-b border-white/10'
        }`;

    // Text colors
    const textColorClass = 'text-white';
    const subTextColorClass = 'text-white/80';
    const hoverColorClass = 'hover:text-white';
    const activeColorClass = 'text-white font-bold';

    // Button Styling
    const loginLinkClass = "px-4 py-2 text-sm font-bold text-white hover:bg-white/10 rounded-lg transition-colors";

    // Logo Box Styling
    const logoBoxClass = isTransparent
        ? "w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"
        : "w-8 h-8 rounded-lg bg-white flex items-center justify-center text-primary";

    const logoTextMain = "text-xl font-bold tracking-tight text-white";
    const logoTextAccent = isTransparent ? "text-primary-300" : "text-white";

    const categories = [
        { label: 'Buy', value: 'buy' },
        { label: 'Rent', value: 'rent' },
        { label: 'Projects', value: 'projects' },
        { label: 'Plots', value: 'plots' },
        { label: 'Civil Services', value: 'civil' },
    ];

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <header className={navbarClasses}>
                <div className="container w-8xl m-auto h-16">
                    <nav className="flex items-center justify-between h-full">

                        {/* Left: Logo */}
                        {/* Left: Logo & City (Home Page Only for City here) */}
                        <div className="flex items-center gap-8 shrink-0 h-full">
                            <Link href="/" className="flex items-center gap-2 group">
                                <div className={logoBoxClass}>
                                    <span className="font-bold text-lg">D</span>
                                </div>
                                <span className={logoTextMain}>
                                    Dream <span className={logoTextAccent}>Acres</span>
                                </span>
                            </Link>
                        </div>

                        {/* Center: Navigation OR Search Bar */}
                        <div className="flex-1 flex justify-center px-8 h-full">
                            {isHome ? (
                                /* HOME PAGE: Original Mega Menu */
                                <div className="hidden lg:flex items-center h-full">

                                    {/* Buy Menu 
                                    <div
                                        className="relative h-full flex items-center px-4"
                                        onMouseEnter={() => handleMouseEnter('buy')}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Link href="/buy" className={`nav-link text-sm font-semibold top-main-menu tracking-wide ${activeDropdown === 'buy' ? activeColorClass : textColorClass} ${hoverColorClass} transition-colors py-2 mb-[1px]`}>
                                            BUY
                                            <div className={`absolute bottom-0 left-0 w-full h-[3px] bg-white scale-x-0 transition-transform origin-left ${activeDropdown === 'buy' ? 'scale-x-100' : ''}`} />
                                        </Link>
                                        {activeDropdown === 'buy' && (
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-[600px] bg-white shadow-dropdown border border-gray-100 rounded-b-xl overflow-hidden animate-fadeInUp origin-top text-left">
                                                <div className="flex">
                                                    <div className="w-1/2 p-6 bg-gray-50/50">
                                                        <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{buyMenuData.left.title}</h6>
                                                        <ul className="space-y-3">
                                                            {buyMenuData.left.links.map((link, i) => (
                                                                <li key={i}>
                                                                    <Link href={link.href} className="text-[15px] text-gray-700 hover:text-primary hover:translate-x-1 transition-all inline-block font-medium">
                                                                        {link.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div className="w-1/2 p-6 bg-white">
                                                        <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{buyMenuData.right.title}</h6>
                                                        <ul className="space-y-3">
                                                            {buyMenuData.right.links.map((link, i) => (
                                                                <li key={i}>
                                                                    <Link href={link.href} className="text-[15px] text-gray-700 hover:text-primary hover:translate-x-1 transition-all inline-block font-medium">
                                                                        {link.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div className="bg-primary/5 p-3 text-center border-t border-primary/10">
                                                    <Link href="/buy" className="text-xs font-bold text-primary hover:underline">View All Properties for Sale &rarr;</Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className="relative h-full flex items-center px-4"
                                        onMouseEnter={() => handleMouseEnter('rent')}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Link href="/rent" className={`nav-link text-sm font-semibold tracking-wide ${activeDropdown === 'rent' ? activeColorClass : textColorClass} ${hoverColorClass} transition-colors py-2 mb-[1px]`}>
                                            RENT
                                            <div className={`absolute bottom-0 left-0 w-full h-[3px] bg-white scale-x-0 transition-transform origin-left ${activeDropdown === 'rent' ? 'scale-x-100' : ''}`} />
                                        </Link>
                                        {activeDropdown === 'rent' && (
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-[600px] bg-white shadow-dropdown border border-gray-100 rounded-b-xl overflow-hidden animate-fadeInUp origin-top text-left">
                                                <div className="flex">
                                                    <div className="w-1/2 p-6 bg-gray-50/50">
                                                        <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{rentMenuData.left.title}</h6>
                                                        <ul className="space-y-3">
                                                            {rentMenuData.left.links.map((link, i) => (
                                                                <li key={i}>
                                                                    <Link href={link.href} className="text-[15px] text-gray-700 hover:text-primary hover:translate-x-1 transition-all inline-block font-medium">
                                                                        {link.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div className="w-1/2 p-6 bg-white">
                                                        <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{rentMenuData.right.title}</h6>
                                                        <ul className="space-y-3">
                                                            {rentMenuData.right.links.map((link, i) => (
                                                                <li key={i}>
                                                                    <Link href={link.href} className="text-[15px] text-gray-700 hover:text-primary hover:translate-x-1 transition-all inline-block font-medium">
                                                                        {link.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div className="bg-primary/5 p-3 text-center border-t border-primary/10">
                                                    <Link href="/rent" className="text-xs font-bold text-primary hover:underline">View All Rental Properties &rarr;</Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    */}

                                    <Link href={`/buy/${selectedCity?.name}?cityId=1`} className={`nav-link px-4 text-sm font-semibold tracking-wide ${textColorClass} ${hoverColorClass} transition-colors`}> BUY </Link>
                                    <Link href={`/rent/${selectedCity?.name}?cityId=1`} className={`nav-link px-4 text-sm font-semibold tracking-wide ${textColorClass} ${hoverColorClass} transition-colors`}> RENT </Link>
                                    <Link href={`/projects/${selectedCity?.name}?cityId=1`} className={`nav-link px-4 text-sm font-semibold tracking-wide ${textColorClass} ${hoverColorClass} transition-colors`}>PROJECTS</Link>
                                    <Link href={`/plots/${selectedCity?.name}?cityId=1`} className={`nav-link px-4 text-sm font-semibold tracking-wide ${textColorClass} ${hoverColorClass} transition-colors`}>PLOTS</Link>
                                    <Link href={`/civil-services/`} className={`nav-link px-4 text-sm font-semibold tracking-wide ${textColorClass} ${hoverColorClass} transition-colors`}>CIVIL SERVICES</Link>
                                </div>
                            ) : isSellerPage ? (
                                /* SELLER PAGES: Seller Account Menu */
                                <div className="hidden lg:flex items-center gap-8 h-full">
                                    {userMenuGroups[1].links
                                        .filter((link) =>
                                        // Projects and Profile are builder-only
                                        (['Projects', 'Profile'].includes(link.label)
                                            ? userRole === 'builder'
                                            : true)
                                        )
                                        .map((link, i) => (
                                            <Link
                                                key={i}
                                                href={link.href}
                                                className={`nav-link text-sm font-semibold tracking-wide whitespace-nowrap ${pathname === link.href ? activeColorClass : textColorClass} ${hoverColorClass} transition-colors py-2 relative group`}
                                            >
                                                {link.label.toUpperCase()}
                                                <div className={`absolute bottom-0 left-0 w-full h-[3px] bg-white transition-transform origin-left ${pathname === link.href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                                            </Link>
                                        ))}
                                </div>
                            ) : (
                                /* INNER PAGES: Professional Search Bar */
                                <div className="hidden lg:flex items-center w-full max-w-4xl mx-auto">
                                    <form onSubmit={handleNavbarSearch} className="flex items-center bg-white shadow-lg w-full min-h-[44px] rounded">

                                        {/* 1. City Filter (Integrated with new typeahead) */}
                                        <div className="flex items-center border-r border-gray-200 pr-2 relative" ref={cityRef}>
                                            <span className="text-gray-500 px-2 pointer-events-none">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                            </span>
                                            <input
                                                className="w-24 text-sm font-bold text-gray-800 bg-transparent border-none outline-none cursor-pointer placeholder-gray-400"
                                                placeholder="City"
                                                value={citySearch}
                                                onFocus={() => setShowCityList(true)}
                                                onChange={(e) => {
                                                    setCitySearch(e.target.value);
                                                    setShowCityList(true);
                                                    setCityIndex(-1);
                                                }}
                                                autoComplete="off"
                                                onKeyDown={handleCityKeyDown}
                                            />
                                            {/* {citySearch && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCitySearch('');
                                                        setSelectedCity("");
                                                        setLocalities([]);
                                                        setSelectedLocalities([]);
                                                        setLocalitySearch('');
                                                        setShowCityList(true);
                                                        setTimeout(() => cityRef.current?.querySelector('input')?.focus(), 0);
                                                    }}
                                                    className="pr-1 text-gray-400 hover:text-red-500 transition-colors focus:outline-none"
                                                >
                                                    ✕
                                                </button>
                                            )} */}
                                            {showCityList && (
                                                <ul className="absolute top-full left-0 z-50 mt-1 w-48 bg-white rounded-md shadow-xl max-h-60 overflow-auto text-left">
                                                    {filteredCities.map((c, i) => (
                                                        <li
                                                            key={c.id}
                                                            id={`nav-city-item-${i}`}
                                                            className={`px-4 py-2 text-sm cursor-pointer transition ${cityIndex === i ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                                            onMouseEnter={() => setCityIndex(i)}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                setCitySearch(c.name);
                                                                setSelectedCity(c);
                                                                setShowCityList(false);
                                                                fetchLocalities(c.id);
                                                                setSelectedLocalities([]);
                                                                setLocalitySearch('');
                                                            }}
                                                        >
                                                            {c.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        {/* 2. Category Dropdown */}
                                        <div className="relative border-r border-gray-200 px-2" onMouseLeave={() => setIsCategoryOpen(false)}>
                                            <button
                                                type="button"
                                                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                                                onMouseEnter={() => setIsCategoryOpen(true)}
                                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 rounded-md transition-colors"
                                            >
                                                <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{selectedCategory.label}</span>
                                                <svg className={`w-3 h-3 text-gray-400 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            {isCategoryOpen && (
                                                <div className="absolute top-full left-0 pt-2 w-40 z-50 animate-fadeIn">
                                                    <div className="bg-white shadow-xl border border-gray-100 py-1 rounded-md overflow-hidden">
                                                        {categories.map(cat => (
                                                            <button
                                                                type="button"
                                                                key={cat.value}
                                                                onClick={() => {
                                                                    setSelectedCategory(cat);
                                                                    setIsCategoryOpen(false);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                                                            >
                                                                {cat.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* 3. Search Input (Locality Multi-Select) */}
                                        <div className="flex-1 flex items-center px-3 relative" ref={localityRef}>
                                            <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 py-1">
                                                {selectedLocalities.map((l) => (
                                                    <div key={l.id} className="flex items-center gap-1 bg-blue-50/50 border border-blue-100 text-blue-700 px-2 py-0.5 rounded text-[13px] whitespace-nowrap">
                                                        <span>{l.name}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeLocality(l.id)}
                                                            className="hover:text-blue-900 focus:outline-none"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                                <input
                                                    className="flex-1 min-w-[100px] text-sm text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent py-1"
                                                    placeholder={selectedLocalities.length === 0 ? "Search by Locality..." : ""}
                                                    value={localitySearch}
                                                    onFocus={() => setShowLocalityList(true)}
                                                    onChange={(e) => {
                                                        setLocalitySearch(e.target.value);
                                                        setShowLocalityList(true);
                                                        setLocalityIndex(-1);
                                                    }}
                                                    autoComplete="off"
                                                    onKeyDown={handleLocalityKeyDown}
                                                />
                                            </div>

                                            {/* Locality Dropdown */}
                                            {showLocalityList && localitySuggestions.length > 0 && (
                                                <ul className="absolute top-full left-0 z-50 mt-1 w-full bg-gray-100 border border-gray-100 shadow-xl max-h-60 overflow-auto text-left">
                                                    {localitySuggestions.map((l, i) => (
                                                        <li
                                                            key={l.id || i}
                                                            id={`nav-locality-item-${i}`}
                                                            className={`px-4 py-2 text-sm cursor-pointer transition ${localityIndex === i ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                                            onMouseEnter={() => setLocalityIndex(i)}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                addLocality(l);
                                                                setLocalitySearch('');
                                                            }}
                                                        >
                                                            {l.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        {/* Search Button */}
                                        <button type="submit" className="bg-primary hover:bg-primary-600 text-white rounded p-2 m-1 shadow-sm transition-all transform hover:scale-105 active:scale-95 shrink-0">
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>

                        {/* Right: Auth & CTA */}
                        <div className="hidden lg:flex items-center gap-4 shrink-0 h-full">
                            <Link href={isSellerPage ? "/seller/post-property" : "/register"} className="btn bg-white text-primary hover:bg-gray-100 py-2 px-4 rounded-full text-xs font-bold uppercase tracking-wide shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all">
                                Post Property <span className="bg-primary/10 px-1.5 py-0.5 rounded ml-1 text-[10px] text-primary">FREE</span>
                            </Link>

                            {(!isLoggedIn && !isProtectedPage) ? (
                                <Link href="/login" className={loginLinkClass}>Login</Link>
                            ) : (
                                <div
                                    className="relative h-full flex items-center"
                                    onMouseEnter={() => handleMouseEnter('user')}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <button className="flex items-center gap-2 group cursor-pointer h-10 px-2 rounded-lg hover:bg-white/10 transition-colors">
                                        <div className="w-8 h-8 rounded-full bg-white text-primary font-bold flex items-center justify-center text-xs border border-white/20 shadow-sm group-hover:scale-105 transition-transform">
                                            {user?.name.replace(/"/g, '')?.charAt(0).toUpperCase() + user?.name.replace(/"/g, '')?.charAt(1).toUpperCase() || 'DA'}
                                        </div>
                                        <svg className={`w-4 h-4 text-white/70 group-hover:text-white transition-transform ${activeDropdown === 'user' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {activeDropdown === 'user' && (
                                        <div className="absolute top-full right-0 w-64 bg-white shadow-dropdown border border-gray-100 rounded-b-xl overflow-hidden animate-fadeInUp origin-top-right z-50">
                                            {/* User Info Header */}
                                            <div className="p-4 bg-gray-50/80 border-b border-gray-100">
                                                <p className="font-bold text-gray-900 leading-tight">{user?.name.replace(/"/g, '')}</p>
                                                <p className="text-xs text-gray-500 truncate mt-1">{user?.mobile.replace(/"/g, '')}</p>
                                            </div>

                                            {/* Menu Sections */}
                                            {/* <div className="py-2">
                                            {userMenuGroups.map((group, groupIdx) => (
                                                <div key={groupIdx} className={groupIdx > 0 ? "mt-2 pt-2 border-t border-gray-50" : ""}>
                                                    <p className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{group.title}</p>
                                                    {group.links.map((link, i) => (
                                                        <Link
                                                            key={i}
                                                            href={link.href}
                                                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors font-medium"
                                                        >
                                                            <span>{link.icon}</span>
                                                            {link.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            ))}
                                        </div> */}

                                            <div className="p-2 border-t border-gray-100 bg-gray-50/30">
                                                <Link
                                                    href={userRole !== 'buyer' ? '/seller/dashboard' : '/my/activity'}
                                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors font-medium"
                                                >
                                                    <span><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg></span>
                                                    My Account
                                                </Link>
                                            </div>
                                            {/* Logout Button */}
                                            <div className="p-2 border-t border-gray-100 bg-gray-50/30">
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-semibold"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                    </svg>
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Mobile Menu Toggle */}
                        <button onClick={() => setIsMobileMenuOpen(true)} className={`lg:hidden p-2 ${textColorClass}`}>
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </nav>
                </div>

                {/* Mobile Menu Drawer (Simplified) */}
                {isMobileMenuOpen && (
                    <div className="fixed inset-0 z-[100] bg-white text-gray-800">
                        <div className="p-4 flex justify-between items-center border-b border-gray-100">
                            <span className="text-xl font-bold text-gray-800">Menu</span>
                            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
                                <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4 flex flex-col gap-4">
                            <Link href="/buy" className="text-lg font-medium text-gray-800" onClick={() => setIsMobileMenuOpen(false)}>Buy</Link>
                            <Link href="/rent" className="text-lg font-medium text-gray-800" onClick={() => setIsMobileMenuOpen(false)}>Rent</Link>
                            <Link href="/projects" className="text-lg font-medium text-gray-800" onClick={() => setIsMobileMenuOpen(false)}>Projects</Link>
                            <Link href="/plots" className="text-lg font-medium text-gray-800" onClick={() => setIsMobileMenuOpen(false)}>Plots</Link>
                            <div className="h-px bg-gray-100 my-2"></div>
                            <Link href="/" className="text-lg font-medium text-gray-800" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
                            <Link href={isSellerPage ? "/seller/post-property" : "/register"} className="btn btn-primary w-full justify-center mt-4" onClick={() => setIsMobileMenuOpen(false)}>Post Property</Link>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
};

export default Navbar;
